import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, Clock, CheckCircle, AlertTriangle,
  Upload, FileText, Calendar, Award
} from 'lucide-react';
import MetricCard from '../components/common/MetricCard';
import ProgressBar from '../components/common/ProgressBar';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';
import { Button } from '../components/ui/button';

const EnhancedNGODashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [credibilityScore, setCredibilityScore] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const userStr = localStorage.getItem('xchange_user');
      const user = JSON.parse(userStr);

      // Fetch dashboard summary
      const summaryRes = await fetch(`${apiUrl}/api/projects/dashboard/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const summary = await summaryRes.json();

      // Fetch projects
      const projectsRes = await fetch(`${apiUrl}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const projectsList = await projectsRes.json();

      // Fetch milestones
      const milestonesRes = await fetch(`${apiUrl}/api/milestones?status=in_progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const milestonesList = await milestonesRes.json();

      // Fetch credibility score
      try {
        const credRes = await fetch(`${apiUrl}/api/matching/ngo/credibility/${user.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (credRes.ok) {
          const credScore = await credRes.json();
          setCredibilityScore(credScore);
        }
      } catch (e) {
        console.log('Credibility score not available yet');
      }

      setDashboardData(summary);
      setProjects(projectsList);
      setMilestones(milestonesList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  const pendingMilestones = milestones.filter(m => m.status === 'in_progress');
  const readyToSubmit = milestones.filter(m => m.status === 'in_progress' && m.completionPercentage >= 90);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">NGO Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage your projects and track deliverables</p>
        </div>

        {/* Credibility Score Banner */}
        {credibilityScore && (
          <div className="bg-gradient-to-r from-teal-500 to-blue-600 rounded-xl shadow-lg p-6 mb-8 text-white">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-2">Your Credibility Score</h2>
                <p className="text-teal-100">Boost your score by completing projects on time and maintaining documentation</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">{credibilityScore.overallScore.toFixed(0)}</div>
                <div className="text-sm text-teal-100">out of 100</div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-6">
              <ScoreComponent label="Documentation" score={credibilityScore.scoreComponents.documentationScore} max={25} />
              <ScoreComponent label="Financial Health" score={credibilityScore.scoreComponents.financialHealthScore} max={25} />
              <ScoreComponent label="Past Performance" score={credibilityScore.scoreComponents.pastPerformanceScore} max={25} />
              <ScoreComponent label="Governance" score={credibilityScore.scoreComponents.governanceScore} max={25} />
            </div>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Active Projects"
            value={dashboardData?.activeProjects || 0}
            subtitle={`${dashboardData?.totalProjects || 0} total`}
            icon={ClipboardList}
            color="#14B8A6"
          />
          <MetricCard
            title="Pending Tasks"
            value={pendingMilestones.length}
            subtitle="Milestones in progress"
            icon={Clock}
            color="#F59E0B"
          />
          <MetricCard
            title="Ready to Submit"
            value={readyToSubmit.length}
            subtitle="Awaiting final submission"
            icon={CheckCircle}
            color="#10B981"
          />
          <MetricCard
            title="Completed"
            value={dashboardData?.completedProjects || 0}
            subtitle="Projects finished"
            icon={Award}
            color="#1E3A5F"
          />
        </div>

        {/* Action Required Section */}
        {readyToSubmit.length > 0 && (
          <AlertBanner
            type="warning"
            message={`${readyToSubmit.length} milestone(s) ready for final submission. Complete them to release funds!`}
            className="mb-6"
          />
        )}

        {/* Active Milestones */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Active Milestones</h2>
          {pendingMilestones.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No active milestones</h3>
              <p className="text-gray-600">All your milestones are up to date!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingMilestones.map((milestone) => (
                <MilestoneCard key={milestone.id} milestone={milestone} navigate={navigate} />
              ))}
            </div>
          )}
        </div>

        {/* Projects Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Projects</h2>
          </div>
          <div className="space-y-4">
            {projects.map((project) => (
              <ProjectCardNGO key={project.id} project={project} navigate={navigate} />
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <QuickActionCard
            icon={Upload}
            title="Upload Evidence"
            description="Submit photos, videos, and documents for milestones"
            onClick={() => navigate('/milestones')}
            color="#14B8A6"
          />
          <QuickActionCard
            icon={FileText}
            title="Generate Reports"
            description="Create impact reports and utilization certificates"
            onClick={() => navigate('/compliance/documents')}
            color="#1E3A5F"
          />
          <QuickActionCard
            icon={Calendar}
            title="Compliance Calendar"
            description="View upcoming deadlines and submissions"
            onClick={() => navigate('/compliance/calendar')}
            color="#10B981"
          />
        </div>
      </div>
    </div>
  );
};

// Score Component for Credibility Banner
const ScoreComponent = ({ label, score, max }) => {
  const percentage = (score / max) * 100;
  return (
    <div>
      <div className="text-sm text-teal-100 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-white bg-opacity-20 rounded-full h-2">
          <div
            className="bg-white h-2 rounded-full transition-all"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm font-semibold">{score.toFixed(0)}/{max}</span>
      </div>
    </div>
  );
};

// Milestone Card Component
const MilestoneCard = ({ milestone, navigate }) => {
  const getDaysUntilDue = () => {
    const due = new Date(milestone.expectedCompletionDate);
    const now = new Date();
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const daysUntil = getDaysUntilDue();
  const isOverdue = daysUntil < 0;
  const isDueSoon = daysUntil >= 0 && daysUntil <= 7;

  return (
    <div 
      className="border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate(`/milestones/${milestone.id}`)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-base font-bold text-gray-900">{milestone.title}</h3>
            <StatusBadge status={milestone.status} size="sm" />
          </div>
          <p className="text-sm text-gray-600">Milestone #{milestone.milestoneNumber}</p>
        </div>
        {(isOverdue || isDueSoon) && (
          <div className={`flex items-center gap-1 text-sm ${isOverdue ? 'text-red-600' : 'text-orange-600'}`}>
            <AlertTriangle className="w-4 h-4" />
            <span>{isOverdue ? `${Math.abs(daysUntil)} days overdue` : `Due in ${daysUntil} days`}</span>
          </div>
        )}
      </div>

      <ProgressBar
        progress={milestone.completionPercentage}
        label="Completion"
        color="auto"
        height="h-2"
      />

      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
        <div>
          <p className="text-xs text-gray-500 mb-1">Budget</p>
          <p className="text-sm font-semibold text-gray-900">₹{(milestone.budgetAllocated / 100000).toFixed(1)}L</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Evidence</p>
          <p className="text-sm font-semibold text-gray-900">{milestone.evidence?.length || 0} items</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Funds Released</p>
          <p className="text-sm font-semibold text-gray-900">₹{(milestone.fundsReleased / 100000).toFixed(1)}L</p>
        </div>
      </div>
    </div>
  );
};

// Project Card Component for NGO
const ProjectCardNGO = ({ project, navigate }) => {
  return (
    <div 
      className="border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-gray-900">{project.title}</h3>
            <StatusBadge status={project.status} size="sm" />
          </div>
          <p className="text-sm text-gray-600 mb-3">{project.projectCode}</p>
          <ProgressBar
            progress={project.completionPercentage || 0}
            label="Completion"
            color="auto"
            height="h-2"
          />
        </div>
        <div className="text-right ml-4">
          <p className="text-2xl font-bold text-teal-600">₹{(project.totalBudget / 100000).toFixed(1)}L</p>
          <p className="text-xs text-gray-500">Total Budget</p>
        </div>
      </div>
    </div>
  );
};

// Quick Action Card
const QuickActionCard = ({ icon: Icon, title, description, onClick, color }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all cursor-pointer group"
    >
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
};

export default EnhancedNGODashboard;
